import { createContext, useContext, useState, useEffect } from "react";
import React from "react";
import { safeStorage } from "../utils/storage";

const LanguageContext = createContext();

const getBrowserLanguage = () => {
  const languages = ["en", "es", "de", "fr", "it", "el", "pl", "pt", "tr"];
  const browserLang = navigator.language.split("-")[0];
  return languages.includes(browserLang) ? browserLang : "en";
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    return safeStorage.get("preferredLanguage") || getBrowserLanguage();
  });
  const [translations, setTranslations] = useState({});

  useEffect(() => {
    safeStorage.set("preferredLanguage", language);
    loadTranslations(language);
  }, [language]);

  const loadTranslations = async (lang) => {
    try {
      const response = await import(`../locales/${lang}.json`);
      setTranslations(response.default);
    } catch (error) {
      console.error(`Failed to load translations for ${lang}:`, error);
      // Fallback to English if translation fails
      if (lang !== "en") {
        const enResponse = await import("../locales/en.json");
        setTranslations(enResponse.default);
      }
    }
  };

  const t = (key, variables) => {
    const keys = key.split(".");
    let value = translations;
    for (const k of keys) {
      value = value?.[k];
    }

    if (!value) return key;
    if (!variables) return value;

    // Handle plural forms first if they exist
    // Format: {count, plural, one {singular form} other {plural form}}
    const pluralRegex =
      /\{(\w+),\s*plural,\s*one\s*\{([^}]*)\}\s*other\s*\{([^}]*)\}\}/g;
    let processedValue = value;

    processedValue = processedValue.replace(
      pluralRegex,
      (match, varName, oneForm, otherForm) => {
        const count = variables[varName];
        if (count === undefined) return match; // Keep original if variable not provided

        return count === 1 ? oneForm : otherForm;
      },
    );

    // Split the string into parts based on variable placeholders (after plural processing)
    const parts = processedValue.split(/(\{[^}]+\})/g);
    let hasJSX = false; // Track if JSX elements exist

    const result = parts.map((part, index) => {
      const match = part.match(/\{(\w+)\}/);
      if (!match) return part; // Return unchanged text

      const varName = match[1];
      const replacement = variables[varName];

      if (replacement === undefined || replacement === null) {
        return match[0]; // Keep placeholder if missing
      }

      if (React.isValidElement(replacement)) {
        hasJSX = true; // JSX detected, we must return an array
        return React.cloneElement(replacement, { key: index });
      }

      return String(replacement); // Ensure non-JSX values are strings
    });

    // If JSX exists, return an array (React fragment-friendly)
    return hasJSX ? result : result.join(""); // Join only if no JSX
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
